import Block from '@mui/icons-material/Block';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Flag from '@mui/icons-material/Flag';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import RemoveDone from '@mui/icons-material/RemoveDone';
import Undo from '@mui/icons-material/Undo';
import Box from '@mui/material/Box';
import {
  BillingLineKind,
  BillingState,
  patchV1CompaniesCompanyIdTicketsId,
  RejectedReason,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useContext, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { BillingLine } from '~hooks/useBillingLines/models';
import useBillingLines from '~hooks/useBillingLines/useBillingLines';
import { useJob } from '~hooks/useJob/useJob';
import useSettlements from '~hooks/useSettlements/useSettlements';
import { Ticket } from '~hooks/useTickets/models';
import {
  ActionsMenuItem,
  ActionsMenuTrigger,
} from '~pages/Approvals/Files/components/JobsTable/Columns/ActionsMenu';
import LoadsQueryContext from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { extractAxiosErrorInfo, isConflictError } from '~utils/extractAxiosError';
import { canEditTicket } from '~utils/tickets/ticket-utils';
import {
  getEffectiveUserCompanyId,
  isViewerAccountCustomer,
} from '~utils/user/user-utils';
import { downloadFile } from '~utils/utilFunctions';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import { sideLabelKeys, toSettlementLines } from '../chargeSelection';
import {
  getLineSettlementNumber,
  getSettlementBlock,
  SettlementBlock,
} from '../groupBillingLines';
import RejectLineDialog from './RejectLineDialog';
import RejectLoadDialog from './RejectLoadDialog';

const rejectLoadBlockedKeys = {
  approved: 'approvals.approve_charges.settlement_blocked.approved',
  draft: 'approvals.approve_charges.settlement_blocked.draft',
  paid: 'approvals.approve_charges.reject_load_blocked.paid',
} satisfies Record<SettlementBlock, string>;

type OpenDialog =
  | { kind: 'mark_not_applied'; line: BillingLine }
  | { kind: 'reject_load' }
  | { kind: 'remove'; line: BillingLine };

interface BillingLineActionsMenuProps {
  isLoadApproved?: boolean;
  lines: BillingLine[];
  loadId?: string;
  loadLines?: BillingLine[];
  ticket?: Ticket;
}

function getLineLabel(line: BillingLine) {
  return t('approvals.approve_charges.line_label', {
    name: line.name ?? '',
    side: t(sideLabelKeys[line.side]),
  });
}

function getRejectLoadBlockedReason(blockingLines: BillingLine[]) {
  if (blockingLines.length === 0) {
    return undefined;
  }

  const { block, settlements } = getSettlementBlock(blockingLines);

  if (!settlements) {
    return t('approvals.approve_charges.reject_load_blocked.cannot_change', {
      count: blockingLines.length,
    });
  }

  return t(rejectLoadBlockedKeys[block], { settlements });
}

export default function BillingLineActionsMenu({
  isLoadApproved = false,
  lines,
  loadId,
  loadLines,
  ticket,
}: BillingLineActionsMenuProps) {
  const { toasterStore, userStore } = useStores();
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const { job, refreshLoads, refreshParentJob, refreshTicketById } =
    useContext(LoadsQueryContext);
  const { rejectBillingLine, unrejectBillingLine } = useBillingLines();
  const { removeLinesFromSettlement } = useSettlements();
  const { unapproveLoad } = useJob();
  const companyId = getEffectiveUserCompanyId(userStore);
  const loadCompanyId = job.company?.id ?? companyId;
  const [openDialog, setOpenDialog] = useState<OpenDialog | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const isUnrejectingRef = useRef(false);
  const menu = useRef<MenuHandler>(null);

  const reportConflict = (error: unknown, fallbackMessage: string) => {
    // Any other failure was reported by the hook. A 409 says the line is on a
    // settlement, and the message of the API says which.
    if (isConflictError(error)) {
      toasterStore.push(
        alert(extractAxiosErrorInfo(error, fallbackMessage).message, AlertTypes.error),
      );
    }
  };

  const finish = () => {
    setIsBusy(false);
    setOpenDialog(null);
    // also after a failure: it may mean the line changed since this page was read
    reload();
  };

  const isLoadMenu = loadLines !== undefined;
  const loadLinesToReject = (loadLines ?? []).filter((line) => !line.isRejected);
  const loadLinesToUnreject = (loadLines ?? []).filter(
    (line) => line.canUnreject && line.rejectedReason === RejectedReason.NEVER_BILL,
  );

  const reloadLoadAndLines = () => {
    reload();
    refreshLoads();
    refreshParentJob();
  };
  const flaggableTicket =
    isLoadMenu &&
    ticket &&
    userStore.getPermissions().canEditTicket &&
    !isViewerAccountCustomer(userStore, job.order?.account) &&
    canEditTicket(ticket.state) &&
    !isLoadApproved
      ? ticket
      : undefined;
  const rejectLoadBlockedReason = getRejectLoadBlockedReason(
    loadLinesToReject.filter(
      (line) => !line.allowedRejectionReasons.includes(RejectedReason.NEVER_BILL),
    ),
  );

  const closeDialogUnlessBusy = () => {
    if (!isBusy) {
      setOpenDialog(null);
    }
  };

  const handleReject = async (line: BillingLine, reason: RejectedReason) => {
    setIsBusy(true);

    try {
      await rejectBillingLine(companyId, line, reason);
      toasterStore.push(
        alert(t('approvals.approve_charges.line_rejected'), AlertTypes.success),
      );
    } catch (error) {
      reportConflict(error, t('error_messages.billing_lines.failed_to_reject'));
    } finally {
      finish();
    }
  };

  const runUnlessUnrejecting = async (unreject: () => Promise<void>) => {
    if (isUnrejectingRef.current) {
      return;
    }

    isUnrejectingRef.current = true;
    setIsBusy(true);

    try {
      await unreject();
    } finally {
      isUnrejectingRef.current = false;
      setIsBusy(false);
    }
  };

  const handleUnreject = async (line: BillingLine) => {
    try {
      await unrejectBillingLine(companyId, line);
      toasterStore.push(
        alert(t('approvals.approve_charges.line_unrejected'), AlertTypes.success),
      );
    } catch (error) {
      reportConflict(error, t('error_messages.billing_lines.failed_to_unreject'));
    } finally {
      reload();
    }
  };

  const unrejectLines = async (linesToUnreject: BillingLine[]) => {
    for (const line of linesToUnreject) {
      await unrejectBillingLine(companyId, line);
    }
  };

  const handleRejectLoad = async () => {
    setIsBusy(true);
    const rejectedLines: BillingLine[] = [];

    try {
      for (const line of loadLinesToReject) {
        await rejectBillingLine(companyId, line, RejectedReason.NEVER_BILL);
        rejectedLines.push(line);
      }

      let isLoadStillApproved = false;

      if (isLoadApproved && loadId) {
        try {
          await unapproveLoad(loadId, loadCompanyId);
        } catch {
          isLoadStillApproved = true;
        }
      }

      toasterStore.push(
        isLoadStillApproved
          ? alert(
              t('approvals.approve_charges.load_rejected_still_approved'),
              AlertTypes.warn,
            )
          : alert(t('approvals.approve_charges.load_rejected'), AlertTypes.success),
      );
    } catch (error) {
      reportConflict(error, t('error_messages.billing_lines.failed_to_reject'));

      const rollbackResults = await Promise.allSettled(
        rejectedLines.map((line) => unrejectBillingLine(companyId, line)),
      );
      const stillRejectedCount = rollbackResults.filter(
        (result) => result.status === 'rejected',
      ).length;

      toasterStore.push(
        stillRejectedCount === 0
          ? alert(t('error_messages.billing_lines.load_not_rejected'), AlertTypes.error)
          : alert(
              t('error_messages.billing_lines.load_partly_rejected', {
                count: stillRejectedCount,
                total: loadLinesToReject.length,
              }),
              AlertTypes.error,
            ),
      );
    } finally {
      setIsBusy(false);
      setOpenDialog(null);
      reloadLoadAndLines();
    }
  };

  const handleUnrejectLoad = async () => {
    try {
      await unrejectLines(loadLinesToUnreject);
      toasterStore.push(
        alert(t('approvals.approve_charges.load_unrejected'), AlertTypes.success),
      );
    } catch (error) {
      reportConflict(error, t('error_messages.billing_lines.failed_to_unreject'));
    } finally {
      reloadLoadAndLines();
    }
  };

  const handleToggleTicketFlag = async (ticketToToggle: Ticket) => {
    setIsBusy(true);

    try {
      await patchV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': companyId, id: ticketToToggle.id },
        body: { flagged: !ticketToToggle.flagged },
      });
      toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
      refreshTicketById(ticketToToggle.id);
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.tickets.failed_to_update'),
        );
      } catch {
        // handleRequestError has already shown the error
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemove = async (line: BillingLine) => {
    const settlementId = line.settlement?.id;
    const settlementLines = toSettlementLines([line]);

    if (!settlementId || settlementLines.length === 0) {
      return;
    }

    setIsBusy(true);

    try {
      // The dialog warns that the last line voids the settlement, so confirming it is
      // the confirmation the API asks for
      await removeLinesFromSettlement(companyId, settlementId, settlementLines, true);
      toasterStore.push(
        alert(
          t('approvals.driver_pay.settlements.lines.line_removed'),
          AlertTypes.success,
        ),
      );
    } catch {
      // the hook has already reported the failure
    } finally {
      finish();
    }
  };

  const ticketImageUrl = ticket?.imageUrl;

  const loadOptions = [
    ...(isLoadMenu
      ? [
          {
            callBack: () => {
              if (ticket && ticketImageUrl) {
                downloadFile(ticket.id, ticketImageUrl, true);
              }
            },
            disabledReason: ticketImageUrl
              ? undefined
              : t('settings.deleted_records.no_ticket_image'),
            icon: FileDownloadOutlined,
            title: t('approvals.files.download_image_cta'),
          },
        ]
      : []),
    ...(flaggableTicket
      ? [
          {
            callBack: () => handleToggleTicketFlag(flaggableTicket),
            icon: flaggableTicket.flagged ? FlagOutlined : Flag,
            title: flaggableTicket.flagged
              ? t('approvals.remove_flag')
              : t('actions.flag'),
          },
        ]
      : []),
    ...(loadLinesToReject.length > 0
      ? [
          {
            callBack: () => setOpenDialog({ kind: 'reject_load' }),
            disabledReason: rejectLoadBlockedReason,
            icon: Block,
            title: t('approvals.approve_charges.actions.reject_load'),
          },
        ]
      : []),
    ...(loadLinesToUnreject.length > 0
      ? [
          {
            callBack: () => runUnlessUnrejecting(handleUnrejectLoad),
            icon: Undo,
            title: t('approvals.approve_charges.actions.unreject_load'),
          },
        ]
      : []),
  ];

  const canRemoveFromDraft = (line: BillingLine) =>
    line.kind !== BillingLineKind.ADD_ON_CHARGE &&
    line.billingState === BillingState.DRAFT &&
    line.settlement !== null;
  const removableSettlementNumbers = lines
    .filter(canRemoveFromDraft)
    .map(getLineSettlementNumber);

  const lineOptions = lines.flatMap((line) => {
    const titled = (action: string) =>
      lines.length > 1
        ? t('approvals.approve_charges.line_action', {
            action,
            line: getLineLabel(line),
          })
        : action;

    const canMarkNotApplied =
      !isLoadMenu && line.allowedRejectionReasons.includes(RejectedReason.NOT_APPLIED);
    const canUnreject =
      !isLoadMenu && line.canUnreject && line.billingState === BillingState.NOT_APPLIED;
    const settlementNumber = getLineSettlementNumber(line);
    const removeTitle = t('approvals.approve_charges.actions.remove_from_settlement', {
      settlement: settlementNumber,
    });
    const sharesSettlementWithAnotherRemovableLine =
      removableSettlementNumbers.filter((number) => number === settlementNumber).length >
      1;

    return [
      ...(canMarkNotApplied
        ? [
            {
              callBack: () => setOpenDialog({ kind: 'mark_not_applied', line }),
              icon: RemoveDone,
              title: titled(t('approvals.approve_charges.actions.mark_not_applied')),
            },
          ]
        : []),
      ...(canUnreject
        ? [
            {
              callBack: () => runUnlessUnrejecting(() => handleUnreject(line)),
              icon: Undo,
              title: titled(t('approvals.approve_charges.actions.unreject')),
            },
          ]
        : []),
      ...(canRemoveFromDraft(line)
        ? [
            {
              callBack: () => setOpenDialog({ kind: 'remove', line }),
              icon: RemoveCircleOutline,
              title: sharesSettlementWithAnotherRemovableLine
                ? titled(removeTitle)
                : removeTitle,
            },
          ]
        : []),
    ];
  });

  const options: Array<{
    callBack: () => unknown;
    disabledReason?: string;
    icon: typeof Block;
    title: string;
  }> = [...loadOptions, ...lineOptions];

  return (
    <Box data-test-id="billing-line-actions" onClick={(event) => event.stopPropagation()}>
      {options.length > 0 && (
        <Menu ref={menu} menuTrigger={<ActionsMenuTrigger isBusy={isBusy} />}>
          {options.map((option) => (
            <ActionsMenuItem
              key={option.title}
              disabledReason={option.disabledReason}
              icon={option.icon}
              iconColor="treadBlack"
              label={option.title}
              onClick={(event) => {
                event.stopPropagation();
                menu.current?.onClose?.();
                void option.callBack();
              }}
            />
          ))}
        </Menu>
      )}

      {openDialog?.kind === 'mark_not_applied' && (
        <RejectLineDialog
          initialReason={RejectedReason.NOT_APPLIED}
          isBusy={isBusy}
          onClose={closeDialogUnlessBusy}
          onConfirm={(reason) => handleReject(openDialog.line, reason)}
          reasons={[RejectedReason.NOT_APPLIED]}
        />
      )}

      {openDialog?.kind === 'reject_load' && (
        <RejectLoadDialog
          isBusy={isBusy}
          isLoadApproved={isLoadApproved}
          lineCount={loadLinesToReject.length}
          onClose={closeDialogUnlessBusy}
          onConfirm={handleRejectLoad}
        />
      )}

      {openDialog?.kind === 'remove' && (
        <ConfirmationDialog
          confirmButtonProps={{
            children: t('approvals.driver_pay.settlements.lines.remove_confirm'),
          }}
          isBusy={isBusy}
          isOpen
          onClose={closeDialogUnlessBusy}
          onConfirm={() => handleRemove(openDialog.line)}
          title={t('approvals.driver_pay.settlements.lines.remove_title')}
        >
          {`${t('approvals.driver_pay.settlements.lines.remove_description')} ${t(
            'approvals.approve_charges.remove_dialog.void_warning',
          )}`}
        </ConfirmationDialog>
      )}
    </Box>
  );
}
